import { useId } from 'react';
import { Check, ImagePlus, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * `local` is a photo chosen before sign-in: previewed from the file in memory
 * and uploaded once the visitor signs in to generate.
 */
export type PhotoSlot = {
  status: 'empty' | 'local' | 'uploading' | 'ready' | 'error';
  preview?: string;
  url?: string;
  file?: File;
};

export const EMPTY_PHOTO_SLOT: PhotoSlot = { status: 'empty' };

export type PhotoPickerLabels = {
  add: string;
  uploading: string;
  remove: string;
  failed: string;
};

/**
 * One portrait photo slot: tap to pick, preview, upload state, remove.
 * `blocked` intercepts the tap (e.g. to open a sign-in gate) instead of
 * opening the file chooser.
 */
export function PhotoPicker({
  label,
  labels,
  slot,
  blocked,
  onBlocked,
  onPick,
  onClear,
}: {
  label: string;
  labels: PhotoPickerLabels;
  slot: PhotoSlot;
  blocked: boolean;
  onBlocked: () => void;
  onPick: (file: File) => void;
  onClear: () => void;
}) {
  const inputId = useId();
  return (
    <div className="relative">
      <label
        htmlFor={inputId}
        onClick={(event) => {
          if (blocked) {
            event.preventDefault();
            onBlocked();
          }
        }}
        className={cn(
          'flex aspect-[4/5] cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border-2 border-dashed border-border bg-muted/40 p-3 text-center transition-colors hover:border-primary/60 hover:bg-primary/5',
          (slot.status === 'ready' || slot.status === 'local') && 'border-solid border-primary/60 p-0',
          slot.status === 'error' && 'border-destructive/60',
        )}
      >
        {slot.preview ? (
          <img src={slot.preview} alt="" className="size-full object-cover" />
        ) : (
          <>
            <ImagePlus className="size-6 text-muted-foreground" aria-hidden="true" />
            <span className="text-xs font-semibold text-foreground">{label}</span>
            <span className="text-xs text-muted-foreground">{labels.add}</span>
          </>
        )}
        {slot.status === 'uploading' ? (
          <span className="absolute inset-0 flex items-center justify-center gap-2 bg-background/70 text-xs font-medium">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {labels.uploading}
          </span>
        ) : null}
        {slot.status === 'ready' || slot.status === 'local' ? (
          <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-xs font-medium">
            <Check className="size-3 text-primary" aria-hidden="true" />
            {label}
          </span>
        ) : null}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onPick(file);
        }}
      />
      {slot.status === 'ready' || slot.status === 'local' || slot.status === 'error' ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={labels.remove}
          className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
      {slot.status === 'error' ? <p className="mt-1 text-xs text-destructive">{labels.failed}</p> : null}
    </div>
  );
}
